import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export function HomeProducts() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="products"
      ref={ref}
      className="py-24 md:py-32 bg-[#2D2D2D]"
      data-testid="home-products-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2
            className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#E8E8E8] mb-4 md:mb-6"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Du bambou pour chaque <span className="text-[#8BC34A]">projet</span>
          </h2>
          <p className="text-sm md:text-lg text-[#9E9E9E] max-w-3xl mx-auto">
            Terrasses, bardages, parquets ou habillages muraux : 
            découvrez nos gammes pensées pour vos espaces intérieurs et extérieurs.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <Link
            to="/nos-produits"
            className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-lg font-bold tracking-wide text-[#2D2D2D] hover:scale-105 transition-all duration-300"
            style={{
              background: 'linear-gradient(145deg, #e0e0e0, #a8a8a8)',
              boxShadow: 'inset 0 0 0 2px #8BC34A, inset 0 1px 3px rgba(255,255,255,0.6), inset 0 -1px 3px rgba(0,0,0,0.15), 0 4px 15px rgba(0,0,0,0.3)',
              border: '3px solid #b0b0b0',
            }}
            data-testid="view-products-btn"
          >
            VOIR NOS PRODUITS
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>

        {/* Nos Realisations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-24 mb-12"
        >
          <h3
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#E8E8E8] mb-4"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Nos <span className="text-[#8BC34A]">projets</span> à La Réunion
          </h3>
          <p className="text-base text-[#9E9E9E] max-w-2xl mx-auto">
            Terrasses et tours de piscine réalisés en bambou REBO sur l'île.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 auto-rows-[250px] md:auto-rows-[280px]">
          {[
            { src: "/images/realisations/piscine-escalier.jpeg", alt: "Escalier piscine bambou", label: "Escalier piscine", span: "row-span-2" },
            { src: "/images/realisations/piscine-jardin.jpeg", alt: "Tour de piscine jardin tropical", label: "Tour de piscine" },
            { src: "/images/realisations/terrasse-piscine-vue.jpeg", alt: "Terrasse piscine vue aerienne", label: "Terrasse piscine" },
            { src: "/images/realisations/piscine-hors-sol.jpeg", alt: "Piscine hors-sol bambou", label: "Piscine hors-sol", span: "row-span-2" },
            { src: "https://customer-assets.emergentagent.com/job_44e06a86-26a6-4a1c-a1e6-5ebd3ec28cc3/artifacts/9dseatqw_Lame%20TROPIC%20Intense%20r%C3%A9alisation.jpeg", alt: "Terrasse bambou Intense", label: "Terrasse Intense" },
            { src: "https://customer-assets.emergentagent.com/job_44e06a86-26a6-4a1c-a1e6-5ebd3ec28cc3/artifacts/nkdsga2i_INTENSE%202.png", alt: "Varangue Intense", label: "Varangue Intense" },
            { src: "https://customer-assets.emergentagent.com/job_44e06a86-26a6-4a1c-a1e6-5ebd3ec28cc3/artifacts/glbi3atl_WhatsApp%20Image%202026-02-06%20at%2012.37.51.jpeg", alt: "Showroom ProBwa", label: "Showroom ProBwa" },
            { src: "https://customer-assets.emergentagent.com/job_44e06a86-26a6-4a1c-a1e6-5ebd3ec28cc3/artifacts/yr7xtfrj_TECK.png", alt: "Tour de piscine Teck", label: "Tour de piscine Teck" },
            { src: "https://customer-assets.emergentagent.com/job_44e06a86-26a6-4a1c-a1e6-5ebd3ec28cc3/artifacts/uhcvpp8n_MIEL2%20%281%29.png", alt: "Terrasse Miel", label: "Terrasse Miel" },
          ].map((item, index) => (
            <motion.div
              key={item.alt}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.7 + index * 0.08 }}
              className={`relative group overflow-hidden rounded-xl ${item.span || ""}`}
              data-testid={`realisation-${index}`}
            >
              <img
                src={item.src}
                alt={item.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <span className="text-white text-sm font-semibold">{item.label}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
